// src/components/EditTimeLogModal.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import Modal from "react-modal";
import Select from "react-select";

// Ensure accessibility for react-modal
Modal.setAppElement("#root");

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const EditTimeLogModal = ({
  isOpen,
  onClose,
  onSubmit,
  timeLog,
  opportunities,
  students,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && timeLog) {
      const student = students.find((s) => s.studentId === timeLog.studentId);
      const opportunity = opportunities.find(
        (o) => o.id === timeLog.opportunityId
      );

      setSelectedStatus(
        timeLog.status
          ? {
              value: timeLog.status.toLowerCase(),
              label:
                timeLog.status.charAt(0).toUpperCase() +
                timeLog.status.slice(1),
            }
          : null
      );
      setSelectedStudent(
        student
          ? {
              value: student.studentId,
              label: `[${student.studentId}] ${student.firstName} ${student.lastName}`,
            }
          : null
      );
      setSelectedOpportunity(
        opportunity
          ? { value: opportunity.id, label: opportunity.hostName }
          : null
      );
      setDate(timeLog.date || "");
      setStartTime(timeLog.startTime || "");
      setEndTime(timeLog.endTime || "");
      setDescription(timeLog.description || "");
    }
  }, [isOpen, timeLog, students, opportunities]);

  // Filter opportunities based on selected student
  const getFilteredOpportunities = () => {
    if (!selectedStudent) return [];

    return opportunities
      .filter((opportunity) =>
        opportunity.students?.includes(selectedStudent.value)
      )
      .sort((a, b) => a.hostName.localeCompare(b.hostName));
  };

  // Create options for the Select component with filtered opportunities
  const opportunityOptions = getFilteredOpportunities().map((opportunity) => ({
    value: opportunity.id,
    label: opportunity.hostName,
  }));

  // Clear selected opportunity when student changes
  useEffect(() => {
    if (!timeLog) {
      setSelectedOpportunity(null);
    }
  }, [selectedStudent, timeLog]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !selectedStatus ||
      !selectedStudent ||
      !selectedOpportunity ||
      !date ||
      !startTime ||
      !endTime
    ) {
      toast.error("Please fill in all fields.");
      return;
    }

    // Validate date (must not be in the future)
    const selectedDate = new Date(date);
    const today = new Date();
    if (selectedDate > today) {
      toast.error("Date cannot be in the future.");
      return;
    }

    // Validate start and end times
    const start = new Date(`${date}T${startTime}`);
    const end = new Date(`${date}T${endTime}`);
    if (start >= end) {
      toast.error("End Time must be after Start Time.");
      return;
    }

    // Calculate total hours
    const diffInMs = end - start;
    const totalHours = diffInMs / (1000 * 60 * 60);

    // Ensure total hours is within a reasonable range
    if (totalHours <= 0 || totalHours > 24) {
      toast.error("Total hours must be between 0 and 24.");
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        ...timeLog,
        status: selectedStatus.value,
        studentId: selectedStudent.value,
        hostId: selectedOpportunity.value,
        hostName: selectedOpportunity.label,
        date,
        startTime,
        endTime,
        totalHours,
        description,
      });

      onClose();
    } catch (error) {
      toast.error("Failed to update time log. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const studentOptions = students.map((student) => ({
    value: student.id,
    label: `[${student.studentId}] ${student.firstName} ${student.lastName}`,
  }));

  // Add function to handle start time change
  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);

    // Calculate end time (start time + 1 hour)
    if (newStartTime) {
      const [hours, minutes] = newStartTime.split(":");
      const startDate = new Date();
      startDate.setHours(parseInt(hours), parseInt(minutes));
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

      // Format end time as HH:mm
      const endHours = endDate.getHours().toString().padStart(2, "0");
      const endMinutes = endDate.getMinutes().toString().padStart(2, "0");
      setEndTime(`${endHours}:${endMinutes}`);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Edit Time Log"
      className="bg-white rounded-lg w-full max-w-md p-6 relative mx-auto my-auto outline-none"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 focus:outline-none"
        aria-label="Close Modal"
      >
        <X size={20} />
      </button>
      {/* Modal Title */}
      <h2 className="text-xl font-bold mb-4">Edit Time Log</h2>
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="status" className="block text-gray-700">
            Status
          </label>
          <Select
            id="status"
            options={STATUS_OPTIONS}
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="Select status"
            className="react-select-container"
            classNamePrefix="react-select"
            isSearchable={false}
          />
        </div>
        <div>
          <label htmlFor="studentId" className="block text-gray-700">
            Student
          </label>
          <Select
            id="studentId"
            options={studentOptions}
            value={selectedStudent}
            onChange={setSelectedStudent}
            placeholder="Select or type a student name or student ID"
            isClearable
            isSearchable
            className="react-select-container"
            classNamePrefix="react-select"
            noOptionsMessage={() => "No students available"}
          />
        </div>
        <div>
          <label htmlFor="hostName" className="block text-gray-700">
            Host Name
          </label>
          <Select
            id="hostName"
            options={opportunityOptions}
            value={selectedOpportunity}
            onChange={setSelectedOpportunity}
            placeholder={
              selectedStudent
                ? "Select or type a host name"
                : "Please select a student first"
            }
            isClearable
            isSearchable
            isDisabled={!selectedStudent}
            className="react-select-container"
            classNamePrefix="react-select"
            noOptionsMessage={() =>
              selectedStudent
                ? "No opportunities available for this student"
                : "Please select a student first"
            }
          />
        </div>
        <div>
          <label htmlFor="date" className="block text-gray-700">
            Date
          </label>
          <input
            id="date"
            type="date"
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>
        <div className="flex justify-between gap-2">
          <div className="w-1/2">
            <label htmlFor="startTime" className="block text-gray-700">
              Start Time
            </label>
            <input
              id="startTime"
              type="time"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={startTime}
              onChange={handleStartTimeChange}
              required
            />
          </div>
          <div className="w-1/2">
            <label htmlFor="endTime" className="block text-gray-700">
              End Time
            </label>
            <input
              id="endTime"
              type="time"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="description" className="block text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            rows="4"
          ></textarea>
        </div>
        {/* Form Buttons */}
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded focus:outline-none"
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`bg-blue-500 text-white font-semibold py-2 px-4 rounded focus:outline-none ${
              loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600"
            }`}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Time Log"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

EditTimeLogModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  timeLog: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    studentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    hostId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    hostName: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    totalHours: PropTypes.number.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.oneOf(["pending", "approved", "rejected"]).isRequired,
  }),
  opportunities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      hostName: PropTypes.string.isRequired,
    })
  ).isRequired,
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      studentId: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default EditTimeLogModal;
